import { isoDate, getLanguageTagBCP47, fromIsoDate, acceptedExpiryDate, reasonableDateOfBirth, validAge, maxValidAge, MagicConfigMixin, validDay, validMonth, validYear } from '@gig/magic-common';
import { __extends, __decorate, __metadata } from 'tslib';
import { Prop, Watch, Emit, Component, Mixins } from 'vue-property-decorator';
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

var DateField;
(function (DateField) {
    DateField[DateField["NONE"] = 0] = "NONE";
    DateField[DateField["DAY"] = 1] = "DAY";
    DateField[DateField["MONTH"] = 2] = "MONTH";
    DateField[DateField["YEAR"] = 3] = "YEAR";
})(DateField || (DateField = {}));
/**
 * Component for the user to enter a date without time. It does not display
 * any calendar to pick it, just 3 inputs.
 * @remarks If using v-wmodel always use the modifier .lazy
 */
var DateComponent = /** @class */ (function (_super) {
    __extends(DateComponent, _super);
    function DateComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.fullDate = {
            day: null,
            month: null,
            year: null,
        };
        _this.currentField = DateField.NONE;
        _this.DateField = DateField;
        return _this;
    }
    DateComponent.prototype.created = function () {
        if (this.value) {
            this.formBuilderChange(this.value);
        }
    };
    DateComponent.prototype.handleInput = function (field, max) {
        if (this.canMoveNext(this.fullDate[field], max)) {
            this.currentField = this.getNextField(field);
        }
    };
    DateComponent.prototype.getTabIndex = function (field) {
        if (!this.reverseMonth) {
            // when the month is not reversed with the day field
            // no special tabindex is needed
            return;
        }
        if (field === 'month') {
            return 1;
        }
        else if (field === 'day') {
            return 2;
        }
        else {
            return 3;
        }
    };
    DateComponent.prototype.getNextField = function (field) {
        if (this.reverseMonth) {
            if (field === 'month') {
                return DateField.DAY;
            }
            else if (field === 'day') {
                return DateField.YEAR;
            }
        }
        else {
            if (field === 'day') {
                return DateField.MONTH;
            }
            else if (field === 'month') {
                return DateField.YEAR;
            }
        }
        return DateField.NONE;
    };
    DateComponent.prototype.handleDelete = function (event, currentField, previousField) {
        var field = this.fullDate[currentField];
        if (field === '' || field === null) {
            event.detail[0].preventDefault();
            this.currentField = previousField;
        }
    };
    Object.defineProperty(DateComponent.prototype, "date", {
        get: function () {
            var invalid = this.$v.fullDate.$invalid;
            if (!invalid) {
                var _a = this.fullDate, day = _a.day, month = _a.month, year = _a.year;
                return isoDate(day, month, year);
            }
            return null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DateComponent.prototype, "hasValue", {
        get: function () {
            return (!!this.fullDate.day || !!this.fullDate.month || !!this.fullDate.year);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DateComponent.prototype, "infoDate", {
        get: function () {
            if (this.date && this.date.length > 8 && this.amendDobHint) {
                var day = parseInt(this.fullDate.day, 10);
                var month = parseInt(this.fullDate.month, 10);
                var year = parseInt(this.fullDate.year, 10);
                var newDate = new Date(Date.UTC(year, month - 1, day));
                var locale = getLanguageTagBCP47(this.getLanguage());
                return day + " " + newDate.toLocaleDateString(locale, {
                    month: 'long',
                }) + " " + year;
            }
            return this.info;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DateComponent.prototype, "hasAgeError", {
        get: function () {
            if (!this.futureDateOnly) {
                return (this.$v.fullDate.$error &&
                    (!this.$v.fullDate.maxAge || !this.$v.fullDate.minAge));
            }
        },
        enumerable: false,
        configurable: true
    });
    DateComponent.prototype.onValueChange = function (value) {
        if (!value || value === this.date) {
            return;
        }
        this.fullDate = fromIsoDate(value);
    };
    DateComponent.prototype.onDateChange = function (date) {
        this.formBuilderChange(date);
        return date;
    };
    DateComponent.prototype.formBuilderChange = function (date) {
        var invalid = this.$v.fullDate.$invalid;
        return {
            isValid: !invalid,
            value: date,
        };
    };
    Object.defineProperty(DateComponent.prototype, "errorMessage", {
        get: function () {
            if (this.hasError && this.message) {
                return this.message;
            }
            if (this.futureDateOnly && !this.$v.fullDate.allowedExpiryDate) {
                return this.$t('ui-date.date.error.expiry-date');
            }
            if (this.enableReasonableDob &&
                !this.$v.fullDate.reasonableDateOfBirth) {
                return this.$t('ui-date.date.invalid');
            }
            if (this.minimumAge && !this.$v.fullDate.minAge) {
                return this.$t('ui-date.date.error.min-age', {
                    minimumAge: this.minimumAge,
                });
            }
            if (this.maximumAge && !this.$v.fullDate.maxAge) {
                return this.$t('ui-date.date.error.max-age', {
                    maximumAge: this.maximumAge,
                });
            }
            return this.$t('ui-date.date.invalid');
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DateComponent.prototype, "hasDateError", {
        get: function () {
            if (this.$v.fullDate.$error && (!this.isOptional || this.hasValue)) {
                return true;
            }
            return this.hasError;
        },
        enumerable: false,
        configurable: true
    });
    DateComponent.prototype.canMoveNext = function (strValue, maximum) {
        var value = parseInt(strValue || '', 10);
        var isMaxLength = (strValue || '').length >= maximum.toString().length;
        if (value === null || Number.isNaN(value)) {
            return false;
        }
        var highestPossibleValid = value * 10 < maximum;
        return (isMaxLength ||
            (value <= maximum && value > 0 && !highestPossibleValid));
    };
    DateComponent.prototype.getLanguage = function () {
        return this.magicConfig.language;
    };
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], DateComponent.prototype, "value", void 0);
    __decorate([
        Prop(Number),
        __metadata("design:type", Number)
    ], DateComponent.prototype, "minimumAge", void 0);
    __decorate([
        Prop(Number),
        __metadata("design:type", Number)
    ], DateComponent.prototype, "maximumAge", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], DateComponent.prototype, "info", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "hasError", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], DateComponent.prototype, "message", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "enableReasonableDob", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "futureDateOnly", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], DateComponent.prototype, "futureDateStart", void 0);
    __decorate([
        Prop(Number),
        __metadata("design:type", Number)
    ], DateComponent.prototype, "futureYearLimit", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "amendDobHint", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "isOptional", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], DateComponent.prototype, "reverseMonth", void 0);
    __decorate([
        Watch('value', { immediate: true }),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", void 0)
    ], DateComponent.prototype, "onValueChange", null);
    __decorate([
        Watch('date'),
        Emit('change'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], DateComponent.prototype, "onDateChange", null);
    __decorate([
        Emit('stand-alone-change'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], DateComponent.prototype, "formBuilderChange", null);
    DateComponent = __decorate([
        Component({
            name: 'ui-date',
            validations: function () {
                var _this = this;
                var validations = {
                    fullDate: {
                        day: {
                            required: required,
                            validDay: validDay,
                        },
                        month: {
                            required: required,
                            validMonth: validMonth,
                        },
                        year: {
                            required: required,
                            validYear: validYear,
                        },
                    },
                };
                if (this.futureDateOnly) {
                    validations.fullDate.allowedExpiryDate = function (model) {
                        return acceptedExpiryDate({
                            model: model,
                            futureYearLimit: _this.futureYearLimit,
                            futureDateStart: _this.futureDateStart
                                ? new Date(_this.futureDateStart)
                                : undefined,
                        });
                    };
                }
                if (this.enableReasonableDob) {
                    validations.fullDate.reasonableDateOfBirth =
                        reasonableDateOfBirth;
                }
                if (this.minimumAge) {
                    validations.fullDate.minAge = validAge(this.minimumAge);
                }
                if (this.maximumAge) {
                    validations.fullDate.maxAge = maxValidAge(this.maximumAge);
                }
                if (this.isOptional && !this.hasValue) {
                    for (var property in validations.fullDate) {
                        validations.fullDate[property] = {};
                    }
                }
                return validations;
            },
        })
    ], DateComponent);
    return DateComponent;
}(Mixins(validationMixin, MagicConfigMixin)));

export default DateComponent;
